/**
 * StripeCardForm — Iter-162 · Phase 1.
 *
 * Inline Stripe Elements card enrollment. Replaces the external
 * Stripe Checkout redirect. Uses CardNumberElement + CardExpiryElement
 * + CardCvcElement (never the single CardElement — the spec calls
 * out the split fields explicitly).
 *
 * Flow:
 *   1. POST /iter156/payment-methods/card/init → SetupIntent client_secret.
 *   2. stripe.confirmCardSetup(client_secret, {payment_method:{card,...}})
 *   3. POST /iter156/payment-methods/card/confirm → mark autopay ACTIVE.
 *
 * REACT_APP_STRIPE_PUBLISHABLE_KEY is the ONLY Stripe key ever
 * touched by the frontend. STRIPE_API_KEY stays server-side.
 */
import { useMemo, useState } from 'react';
import { Elements, useElements, useStripe,
         CardNumberElement, CardExpiryElement, CardCvcElement } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { Button } from '../ui/button';
import { Loader2, CreditCard, ShieldCheck } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Cache the Stripe.js loader singleton per publishable key.
let _stripePromise = null;
function getStripePromise() {
  const key = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
  if (!key) return null;
  if (!_stripePromise) _stripePromise = loadStripe(key);
  return _stripePromise;
}

const ELEMENT_OPTIONS = {
  style: {
    base: {
      fontSize: '14px', color: '#e5e5e5',
      '::placeholder': { color: '#71717a' },
      fontFamily: 'ui-sans-serif, system-ui, sans-serif',
    },
    invalid: { color: '#f87171' },
  },
};

function CardFormInner({ property_id, customer_id, customer_email, onEnrolled, onCancel }) {
  const stripe = useStripe();
  const elements = useElements();
  const [name, setName] = useState(customer_id || '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setBusy(true); setErr(''); setMsg('');
    try {
      // 1. Ask backend for a SetupIntent.
      const initRes = await fetch(`${API}/api/iter156/payment-methods/card/init`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ property_id, customer_id, customer_email }),
      });
      const initJson = await initRes.json();
      if (initJson.status !== 'ready') {
        setErr(initJson.note || initJson.detail
              || (initJson.status === 'stripe_error'
                    ? `Card setup failed (${initJson.step}).`
                    : 'Card setup temporarily unavailable.'));
        setBusy(false); return;
      }
      // 2. Confirm on the client with the split card elements.
      const cardNumber = elements.getElement(CardNumberElement);
      const result = await stripe.confirmCardSetup(initJson.client_secret, {
        payment_method: {
          card: cardNumber,
          billing_details: { name: name || customer_id, email: customer_email || undefined },
        },
      });
      if (result.error) {
        setErr(result.error.message || 'Card verification failed.');
        setBusy(false); return;
      }
      // 3. Server-side confirm to flip autopay ACTIVE.
      const confirmRes = await fetch(`${API}/api/iter156/payment-methods/card/confirm`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ property_id, setup_intent_id: result.setupIntent.id }),
      });
      const conf = await confirmRes.json();
      if (conf.status === 'active') {
        setMsg(`Card ${conf.card_brand?.toUpperCase()} ****${conf.card_last4} saved. Autopay active.`);
        onEnrolled && onEnrolled(conf);
      } else {
        setErr(conf.message || 'Card enrollment could not complete.');
      }
    } catch (ex) {
      setErr(String(ex.message || ex).slice(0, 200));
    }
    setBusy(false);
  };

  return (
    <form onSubmit={submit} className="space-y-3" data-testid="stripe-card-form">
      <div>
        <label className="text-[10px] uppercase tracking-widest text-zinc-400">Cardholder name</label>
        <input
          type="text" value={name} onChange={(e) => setName(e.target.value)}
          className="w-full rounded bg-zinc-900 border border-zinc-700 text-white text-sm p-2"
          placeholder="Name on card"
          data-testid="stripe-card-name"
        />
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-widest text-zinc-400">Card number</label>
        <div className="rounded bg-zinc-900 border border-zinc-700 p-2" data-testid="stripe-card-number">
          <CardNumberElement options={ELEMENT_OPTIONS} />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-zinc-400">Expiry</label>
          <div className="rounded bg-zinc-900 border border-zinc-700 p-2" data-testid="stripe-card-expiry">
            <CardExpiryElement options={ELEMENT_OPTIONS} />
          </div>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-zinc-400">CVC</label>
          <div className="rounded bg-zinc-900 border border-zinc-700 p-2" data-testid="stripe-card-cvc">
            <CardCvcElement options={ELEMENT_OPTIONS} />
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2 pt-1">
        <Button type="submit" disabled={busy || !stripe}
                className="text-[11px] uppercase font-bold tracking-widest"
                style={{ backgroundColor: '#8b5cf6', color: '#fff' }}
                data-testid="stripe-card-submit">
          {busy ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <CreditCard className="h-3 w-3 mr-1" />}
          Save & Enroll Card Autopay
        </Button>
        {onCancel && (
          <Button type="button" onClick={onCancel}
                  className="text-[11px] uppercase"
                  style={{ backgroundColor: '#3f3f46', color: '#fff' }}
                  data-testid="stripe-card-cancel">
            Cancel
          </Button>
        )}
        <span className="ml-auto flex items-center text-[10px] text-zinc-500">
          <ShieldCheck className="h-3 w-3 mr-1 text-emerald-400" /> Card data handled by Stripe
        </span>
      </div>
      {msg && <p className="text-xs" style={{ color: '#7CFC8A' }} data-testid="stripe-card-msg">{msg}</p>}
      {err && <p className="text-xs text-red-400" data-testid="stripe-card-err">{err}</p>}
    </form>
  );
}

export default function StripeCardForm(props) {
  const stripePromise = useMemo(() => getStripePromise(), []);
  if (!stripePromise) {
    return (
      <div className="rounded border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-amber-200"
           data-testid="stripe-card-key-missing">
        Stripe publishable key missing. Populate <code>REACT_APP_STRIPE_PUBLISHABLE_KEY</code> via
        Publish → Secrets to enable card autopay.
      </div>
    );
  }
  return (
    <Elements stripe={stripePromise}>
      <CardFormInner {...props} />
    </Elements>
  );
}
